import React from "react";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";
import {
  Plane,
  Hotel,
  MapPin,
  CheckCircle2,
  PhoneCall,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import DropOffHero from "../assets/gallery/image-8.jpeg"; // replace with a relevant image

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { delay },
});

export const DropOffPickupServices = () => {
  return (
    <MainLayout>
      {/* Hero Section */}
      <section
        className="relative h-[70vh] rounded-3xl mx-4 sm:mx-10 my-6 overflow-hidden shadow-2xl flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${DropOffHero})` }}
      >
        <div className="absolute inset-0 bg-black/70 z-0" />
        <div className="relative z-10 text-center text-white max-w-3xl px-6">
          <motion.h1
            {...fadeInUp(0.1)}
            className="text-4xl sm:text-6xl font-bold mb-4 just-me-again-down-here-regular"
          >
            Drop-off & Pick-up Services
          </motion.h1>
          <motion.p
            {...fadeInUp(0.3)}
            className="text-lg sm:text-xl text-white/90"
          >
            On-Time, Door-to-Door Transfers – Wherever You Need to Be
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-5xl mx-auto px-4 py-12 text-center">
        <motion.p
          {...fadeInUp(0.1)}
          className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"
        >
          Our drop-off and pick-up service is designed to make your arrival or
          departure smooth and stress-free. Whether you’re flying in or heading
          out, we’ll get you to your destination comfortably and on time.
        </motion.p>
      </section>

      {/* Services Section */}
      <section className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-8 pb-20">
        {[
          {
            icon: <Plane className="w-10 h-10 text-teal-600" />,
            title: "Airport Pick-up",
            desc: "We’ll be ready and waiting when you land — no delays, no stress. Our drivers meet you at the airport and offer a seamless ride to your hotel or preferred location in Kampala.",
          },
          {
            icon: <Hotel className="w-10 h-10 text-teal-600" />,
            title: "Hotel & Town Drop-offs",
            desc: "Need to get to a meeting, restaurant, or event in town? We’ve got you covered. Point-to-point rides within Kampala are available upon request.",
          },
          {
            icon: <MapPin className="w-10 h-10 text-teal-600" />,
            title: "Drop-off Outside Kampala",
            desc: "We can take you wherever you need to go – outside Kampala city limits for an additional fee, depending on distance and route.",
          },
        ].map((item, index) => (
          <motion.div
            key={index}
            {...fadeInUp(index * 0.2)}
            className="p-8 rounded-2xl bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 shadow-xl hover:scale-[1.02] transition"
          >
            <div className="mb-4 flex justify-center">{item.icon}</div>
            <h3 className="text-xl font-bold mb-3 just-me-again-down-here-regular text-center">
              {item.title}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm text-center">
              {item.desc}
            </p>
          </motion.div>
        ))}
      </section>

      {/* What's Included Section */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <motion.h2
          {...fadeInUp(0.1)}
          className="text-3xl font-bold text-center mb-8 just-me-again-down-here-regular"
        >
          ✔️ What’s Included
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            "Flight tracking for timely airport pickups",
            "Friendly, professional drivers",
            "Private, clean, and comfortable vehicles",
            "Optional luggage assistance",
          ].map((text, i) => (
            <motion.div
              key={i}
              {...fadeInUp(0.2 + i * 0.1)}
              className="flex items-start gap-3"
            >
              <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700 dark:text-gray-300 text-lg">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center py-16 px-6 bg-gradient-to-br from-teal-700 to-teal-900 text-white rounded-3xl mx-4 sm:mx-10 my-10 shadow-2xl">
        <motion.h3
          {...fadeInUp(0.1)}
          className="text-3xl md:text-5xl font-bold mb-4 just-me-again-down-here-regular"
        >
          Let us know your schedule and destination
        </motion.h3>
        <motion.p
          {...fadeInUp(0.2)}
          className="max-w-3xl mx-auto text-lg text-white/90 mb-8"
        >
          We’ll take care of the rest — from airport pickups to drop-offs
          anywhere you need to be.
        </motion.p>
        <motion.div
          {...fadeInUp(0.3)}
          className="flex flex-wrap justify-center gap-4"
        >
          <Link
            to="/contact"
            className="bg-white text-teal-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition flex items-center gap-2"
          >
            Book a Ride <ArrowRight className="w-5 h-5" />
          </Link>

          {/* WhatsApp Link */}
          <a
            href={`https://wa.me/256785102674?text=${encodeURIComponent(
              "Hello Muhaad! I'm interested in your drop-off & pick-up services."
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-teal-600 hover:bg-teal-500 px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
          >
            <PhoneCall className="w-5 h-5" /> Talk to Us
          </a>
        </motion.div>
      </section>
    </MainLayout>
  );
};
